import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import HStack from '@/components/Layout/HStack';
import VStack from '@/components/Layout/VStack';
import type { NavigationSectionGroup } from '@/components/Navigation/NavigationCard';
import NavigationCard from '@/components/Navigation/NavigationCard';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useInboundDataPipelineDetail } from '../../-query';
import { DomainTypeSettingsSection } from './-components/DomainTypeSettingsSection';
import { MappingSettingsSection } from './-components/MappingSettingsSection';
import { SourceFieldDetectionSection } from './-components/SourceFieldDetectionSection';
import { UniqueKeySettingsSection } from './-components/UniqueKeySettingsSection';
export const Route = createFileRoute('/_staff/settings/data-connector/inbound/data-pipeline/$id/_pipeline-detail/data-link-settings/')({
  component: DataLinkSettings
});

/**
 * pipeline.dataType ごとに表示するセクション定義。
 * セクションの順序: ユニークキー → ドメイン種別 → マッピング。
 * - 値変換（ValueMapping）はマッピング設定セクション内に同居する。
 * - PEOPLE / DEVICE: ドメイン種別セクションが追加される
 * - ORGANIZATION / FILE: ドメイン種別セクションは無し
 */
type SectionDef = {
  kind: 'source-detection';
  hash: 'source-detection';
} | {
  kind: 'unique-key';
  hash: 'unique-key-settings';
} | {
  kind: 'domain-type';
  variant: 'PEOPLE' | 'DEVICE';
  hash: 'domain-type-settings';
} | {
  kind: 'mapping';
  hash: 'mapping-settings';
};
type DataType = 'PEOPLE' | 'DEVICE' | 'ORGANIZATION' | 'FILE';
const SECTIONS_BY_DATA_TYPE: Record<DataType, readonly SectionDef[]> = {
  PEOPLE: [{
    kind: 'source-detection',
    hash: 'source-detection'
  }, {
    kind: 'unique-key',
    hash: 'unique-key-settings'
  }, {
    kind: 'domain-type',
    variant: 'PEOPLE',
    hash: 'domain-type-settings'
  }, {
    kind: 'mapping',
    hash: 'mapping-settings'
  }],
  DEVICE: [{
    kind: 'source-detection',
    hash: 'source-detection'
  }, {
    kind: 'unique-key',
    hash: 'unique-key-settings'
  }, {
    kind: 'domain-type',
    variant: 'DEVICE',
    hash: 'domain-type-settings'
  }, {
    kind: 'mapping',
    hash: 'mapping-settings'
  }],
  ORGANIZATION: [{
    kind: 'source-detection',
    hash: 'source-detection'
  }, {
    kind: 'unique-key',
    hash: 'unique-key-settings'
  }, {
    kind: 'mapping',
    hash: 'mapping-settings'
  }],
  FILE: [{
    kind: 'source-detection',
    hash: 'source-detection'
  }, {
    kind: 'unique-key',
    hash: 'unique-key-settings'
  }, {
    kind: 'mapping',
    hash: 'mapping-settings'
  }]
};
function DataLinkSettings() {
  return <Suspense>
      <Content />
    </Suspense>;
}
function Content() {
  const {
    id
  } = Route.useParams();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector'
  });
  useDocumentTitle(t('inboundDataPipeline.detail.dataLinkSettings.title'));
  const {
    data: detail
  } = useInboundDataPipelineDetail(id);
  const dataType = detail.basicInfo.dataType;
  const sections = dataType in SECTIONS_BY_DATA_TYPE ? SECTIONS_BY_DATA_TYPE[dataType] : [];
  const navigationSections: NavigationSectionGroup[] = sections.map(section => {
    let titleLabel: string;
    switch (section.kind) {
      case 'source-detection':
        titleLabel = t('inboundDataPipeline.detail.dataLinkSettings.sourceDetection.title');
        break;
      case 'unique-key':
        titleLabel = t('inboundDataPipeline.detail.dataLinkSettings.uniqueKeySettings.title');
        break;
      case 'domain-type':
        titleLabel = section.variant === 'PEOPLE' ? t('inboundDataPipeline.detail.dataLinkSettings.domainTypeSettings.title.PEOPLE') : t('inboundDataPipeline.detail.dataLinkSettings.domainTypeSettings.title.DEVICE');
        break;
      case 'mapping':
        titleLabel = t('inboundDataPipeline.detail.dataLinkSettings.title');
        break;
    }
    return {
      titleLabel,
      to: '/settings/data-connector/inbound/data-pipeline/$id/data-link-settings',
      hash: section.hash
    };
  });
  return <HStack className="gap-9 px-12 py-6">
      <div className="sticky top-6 w-52 shrink-0">
        <NavigationCard sections={navigationSections} />
      </div>

      <VStack className="flex-1 min-w-0 gap-6">
        {sections.map(section => <SectionRenderer key={section.hash} section={section} pipelineId={id} />)}
      </VStack>
    </HStack>;
}
function SectionRenderer({
  section,
  pipelineId
}: {
  section: SectionDef;
  pipelineId: string;
}) {
  switch (section.kind) {
    case 'source-detection':
      return <SourceFieldDetectionSection pipelineId={pipelineId} />;
    case 'unique-key':
      return <UniqueKeySettingsSection pipelineId={pipelineId} />;
    case 'domain-type':
      return <DomainTypeSettingsSection pipelineId={pipelineId} variant={section.variant} />;
    case 'mapping':
      return <MappingSettingsSection pipelineId={pipelineId} />;
  }
}